import { useCallback, useEffect, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../redux/store"
import {
  getEvents,
  setCurrentPage,
} from "../../redux/slices/events/eventsSlice"
import { IEvent, IEventsRequest } from "../../components/Events/types"

interface UseInfiniteEventsParams {
  filter: IEventsRequest
}

interface UseInfiniteEventsReturn {
  // Merged list of all pages fetched so far for the current filter. This is
  // the source of truth for what the UI should render.
  events: IEvent[]
  // Initial load for the current filter (first page still in flight). We
  // deliberately distinguish from `isAppending` so the UI can show the
  // full-page preloader once, then switch to an inline spinner at the foot
  // of the list for subsequent pages.
  isInitialLoading: boolean
  // A non-first page is being fetched — used to show the small bottom-of-
  // list spinner without disrupting what's already rendered.
  isAppending: boolean
  // True once every page has been loaded for the current filter — lets the
  // UI swap the spinner for a quiet "end of list" footer.
  hasReachedEnd: boolean
  // Total events across all pages for the current filter; used by the
  // header's calibration subtitle ("3 events · 1 happening today").
  total: number
  // Imperative fetch-next-page trigger, called by the scroll sentinel.
  fetchNext: () => void
}

// Ceiling for the auto-drain loop below. Accounts up to this many pages
// get their full list pulled in progressively after mount — no scroll
// required. Past this size, the scroll sentinel takes over so we don't
// slam the API or the main thread for power users with massive lists.
// Sized for Mazoom's realistic distribution: ~95% of organizers fit in
// a single page, 4-5 pages covers the long tail comfortably.
const MAX_AUTO_PAGES = 20

// Owns the infinite-scroll concerns so EventsPage stays compositional.
//
// Why local accumulation: the events slice replaces `state.data` on every
// fetch (shared with other consumers we don't want to disturb), so we
// accumulate pages here. When the filter changes we reset the accumulator
// to page-1's payload; subsequent pages append, deduped by event_id.
export const useInfiniteEvents = ({
  filter,
}: UseInfiniteEventsParams): UseInfiniteEventsReturn => {
  const dispatch = useAppDispatch()
  const { data, total_pages, current_page, isLoading, total } = useAppSelector(
    (state) => state.events,
  )

  const [accumulated, setAccumulated] = useState<IEvent[]>([])
  // Track the last page we folded into the accumulator so we don't double-
  // append if React renders the effect twice with the same data reference.
  const lastMergedPage = useRef<number>(0)
  // Track the filter signature behind the current accumulator so we know
  // when to reset (different search/sort/status = different stream).
  const currentFilterKey = useRef<string>("")
  // Avoid firing overlapping fetches from the scroll sentinel — one in-
  // flight at a time is plenty, and stops the API from being hammered.
  const isFetchingRef = useRef<boolean>(false)

  const filterKey = buildFilterKey(filter)

  // Reset the stream whenever the filter signature changes. We clear the
  // accumulator and explicitly request page 1 so the cursor is anchored.
  useEffect(() => {
    if (currentFilterKey.current === filterKey) return
    currentFilterKey.current = filterKey
    lastMergedPage.current = 0
    setAccumulated([])
    isFetchingRef.current = true
    dispatch(setCurrentPage(1))
    dispatch(getEvents(filter)).finally(() => {
      isFetchingRef.current = false
    })
  }, [dispatch, filterKey, filter])

  // Fold each incoming Redux payload into the accumulator.
  //
  // Rules:
  //   • current_page === 1  → replace (fresh query or filter reset)
  //   • current_page >  lastMergedPage → append (next page arrived)
  //   • current_page <= lastMergedPage → ignore (same payload or stale)
  //
  // Dedupe by event_id because page boundaries can shift between fetches
  // (e.g. when a new event is created mid-session).
  useEffect(() => {
    if (isLoading) return
    if (data.length === 0 && current_page === 1) {
      setAccumulated([])
      lastMergedPage.current = 1
      return
    }
    if (current_page === 1) {
      setAccumulated(data)
      lastMergedPage.current = 1
      return
    }
    if (current_page > lastMergedPage.current) {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((e) => e.event_id))
        const merged = [...prev]
        data.forEach((event) => {
          if (!seen.has(event.event_id)) {
            merged.push(event)
            seen.add(event.event_id)
          }
        })
        return merged
      })
      lastMergedPage.current = current_page
    }
  }, [data, current_page, isLoading])

  const hasReachedEnd =
    lastMergedPage.current > 0 && lastMergedPage.current >= total_pages

  const fetchNext = useCallback(() => {
    if (isFetchingRef.current) return
    if (hasReachedEnd) return
    if (isLoading) return
    isFetchingRef.current = true
    const nextPage = lastMergedPage.current + 1
    dispatch(setCurrentPage(nextPage))
    dispatch(getEvents(filter)).finally(() => {
      isFetchingRef.current = false
    })
  }, [dispatch, filter, hasReachedEnd, isLoading])

  // Auto-drain pagination after the first page lands.
  //
  // The scroll sentinel alone is insufficient when the rendered list
  // doesn't fill the viewport beyond its intersection margin: the user
  // sees a partial result (e.g. "All 15" while the header already says
  // "25 events") and their tab counts and search results are computed
  // off an incomplete accumulator. Draining the remaining pages in the
  // background fixes the coherence problem at the source — by the time
  // the user interacts with tabs, filters, or search, the full dataset
  // is in memory.
  //
  // MAX_AUTO_PAGES caps the drain for very large accounts so we don't
  // fetch the entire Earth into memory; past that, the scroll sentinel
  // continues to handle the tail organically.
  useEffect(() => {
    if (isLoading) return
    if (hasReachedEnd) return
    if (accumulated.length === 0) return
    if (isFetchingRef.current) return
    if (lastMergedPage.current >= MAX_AUTO_PAGES) return
    fetchNext()
  }, [accumulated.length, hasReachedEnd, isLoading, fetchNext])

  // The UI treats "initial loading" as "the accumulator is still empty AND
  // the first fetch hasn't resolved yet" — that's when a full-page loader
  // makes sense. Every subsequent page goes through the inline spinner.
  const isInitialLoading = isLoading && accumulated.length === 0
  const isAppending = isLoading && accumulated.length > 0

  return {
    events: accumulated,
    isInitialLoading,
    isAppending,
    hasReachedEnd,
    total,
    fetchNext,
  }
}

// Stable key from the parts of the filter that should invalidate the
// accumulator. Any change to sort field, direction, search, or status
// means a fresh stream — the backend will reorder the whole list and
// previously-fetched pages no longer align with the new order.
const buildFilterKey = (filter: IEventsRequest): string =>
  [filter.sortField, filter.sortDir, filter.searchValue, filter.status].join(
    "|",
  )
